import { useCallback, useEffect, useMemo } from "react";
import { Gesture } from "react-native-gesture-handler";
import { runOnJS } from "react-native-reanimated";
import {
	GestureComposer,
	KeyExtractorParams,
	RenderItemParams,
} from "src/components/ImageGallery/types";
import useDevice from "src/hooks/useDevice";
import CarouselPhotoItem from "../components/CarouselPhotoItem";
import MainPhotoItem from "../components/MainPhotoItem";
import usePhotoDetailStore from "../stores/usePhotoDetailStore";
import { PhotoItem } from "../types";
import useZoomGestureHandlers from "./useZoomGestureHandlers";

const useImageGalleryProps = ({ photoId }: { photoId: string }) => {
	const keyExtractor = useCallback(({ item }: KeyExtractorParams<PhotoItem>) => {
		if (!item) {
			throw new Error("item in keyExtractor is undefined");
		}
		return item.photoId;
	}, []);

	const renderMainItem = useCallback(
		({ item, ...props }: RenderItemParams<PhotoItem>) => {
			if (!item) {
				throw new Error("item in renderMainItem is undefined");
			}
			return <MainPhotoItem {...props} photo={item} isInitial={item.photoId === photoId} />;
		},
		[photoId],
	);
	const renderCarouselItem = useCallback(({ item, ...props }: RenderItemParams<PhotoItem>) => {
		if (!item) {
			throw new Error("item in renderCarouselItem is undefined");
		}
		return <CarouselPhotoItem {...props} photo={item} />;
	}, []);
	const handleMainTap = useMemo(() => {
		const toggleCarousel = () => {
			const { mode, setMode } = usePhotoDetailStore.getState();
			switch (mode) {
				case "Carousel":
					setMode("Regular");
					return;
				case "FullScreen":
					return;
				case "Zoom":
				case "Regular":
				default:
					setMode("Carousel");
					return;
			}
		};
		return () => {
			"worklet";
			runOnJS(toggleCarousel)();
		};
	}, []);

	const {
		handleZoomPinchStart,
		handleZoomPinchChange,
		handleZoomPinchEnd,
		handleZoomPanStart,
		handleZoomPanChange,
		handleZoomPanEnd,
		handleDoubleTapEnd,
	} = useZoomGestureHandlers();
	const mode = usePhotoDetailStore((state) => state.mode);
	const { device } = useDevice();

	const createMainContentGesture = useCallback(() => {
		const tap = Gesture.Tap()
			.onEnd(handleMainTap)
			.enabled(mode === "Regular" || mode === "Carousel");
		if (device === "desktop") {
			return tap;
		}
		const doubleTap = Gesture.Tap().numberOfTaps(2).onEnd(handleDoubleTapEnd);
		const zoomPinch = Gesture.Pinch()
			.onStart(handleZoomPinchStart)
			.onChange(handleZoomPinchChange)
			.onEnd(handleZoomPinchEnd);
		const zoomPan = Gesture.Pan()
			.onStart(handleZoomPanStart)
			.onChange(handleZoomPanChange)
			.onEnd(handleZoomPanEnd)
			// This helps fix the slingshot bug on web
			.minPointers(1)
			.maxPointers(1)
			.enabled(mode === "Zoom");
		return Gesture.Race(
			// Having this nested Race seems to reduce the likelihood of a weird pinch slingshot bug
			Gesture.Race(zoomPinch, zoomPan),
			Gesture.Exclusive(doubleTap, tap),
		);
	}, [
		device,
		handleDoubleTapEnd,
		handleMainTap,
		handleZoomPanChange,
		handleZoomPanEnd,
		handleZoomPanStart,
		handleZoomPinchChange,
		handleZoomPinchEnd,
		handleZoomPinchStart,
		mode,
	]);
	useEffect(() => {
		usePhotoDetailStore.setState({
			createMainContentGesture,
		});
	}, [createMainContentGesture]);

	const mainGestureComposer = useCallback<GestureComposer>(
		(originalPan) => {
			const pan = originalPan.enabled(mode === "Regular" || mode === "Carousel");
			return pan;
		},
		[mode],
	);

	return {
		keyExtractor,
		renderMainItem,
		renderCarouselItem,
		mainGestureComposer,
	};
};
export default useImageGalleryProps;
